import * as React from "react";
import { BookOpen, RefreshCw } from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { useAdminReadLogs } from "@/lib/learning/adminApi";

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(iso),
  );
}

export function LearningReadLogsTab() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "learning-read-logs",
    defaultPageSize: 25,
  });
  const query = useAdminReadLogs({ limit: pageSize, offset: page * pageSize });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <DataTable>
      <DataTableToolbar>
        <DataTableToolbarSpacer />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
        >
          <RefreshCw
            aria-hidden="true"
            className={cn("size-4", query.isFetching && "animate-spin")}
          />
          Refresh
        </Button>
        <span className="text-muted-foreground text-xs tabular-nums">
          {total.toLocaleString()} logs
        </span>
      </DataTableToolbar>
      <DataTableScroll>
        <Table>
          <caption className="sr-only">Learner reading logs</caption>
          <TableHeader>
            <TableRow>
              <TableHead>Learner</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Topic</TableHead>
              <TableHead className="text-right">Minutes</TableHead>
              <TableHead>Logged</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          {query.isLoading ? (
            <DataTableSkeleton rows={8} columns={6} />
          ) : (
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.learner_display_name ?? "Learner"}
                  </TableCell>
                  <TableCell>{row.subject}</TableCell>
                  <TableCell>{row.topic}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.minutes_spent ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {formatWhen(row.logged_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[240px] truncate text-xs">
                    {row.note ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </DataTableScroll>
      {!query.isLoading && items.length === 0 ? (
        <DataTableEmpty
          icon={<BookOpen aria-hidden="true" />}
          title="No reading logs yet"
          description="Logs appear when learners record what they studied in the portal."
        />
      ) : null}
      {!query.isLoading && items.length > 0 ? (
        <DataTableFooter>
          <DataTablePagination
            page={page + 1}
            pageSize={pageSize}
            totalRows={total}
            onPageChange={(next) => setPage(Math.max(0, next - 1))}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(0);
            }}
            testIdPrefix="learning-read-logs"
          />
        </DataTableFooter>
      ) : null}
    </DataTable>
  );
}
